import {createAction} from '@packages/utilities/create-action';
import {
  VIDEO_SELECTED_FOR_UPLOAD,
  ASSET_SIGNING_FINISHED,
  ASSET_UPLOAD_FINISHED,
  ASSET_SIGNING_FAILED,
  ASSET_UPLOAD_FAILED,
  VIDEO_READ_FOR_VALIDATION_FAILED,
  ASSET_S3_UPLOAD_NOT_NEEDED,
} from '@packages/systems/designer/actions';
import {scheduleTranscode, ScheduledTranscode} from './UploadVideoUtils';
import {
  uploadSiteVideo,
  type singleAssetActionCreator,
} from './UploadVideoHelpers';
import {FileRecord} from '@packages/systems/core/records/FileRecord';
import {type AssetHelperFunctions} from '@packages/shared/utils/AssetUtils';
import {AssetsActionCreators} from '@packages/systems/designer/flux-action-creators';

const _cancels: Record<string, any> = {};

const videoSelectedForUpload: singleAssetActionCreator = createAction(
  VIDEO_SELECTED_FOR_UPLOAD
);

const getSiteId = (flux: any) => {
  return () => flux.getStoreState('SiteDataStore').siteId;
};

export const getCachedUploadCancel = (fileId: string) => {
  return _cancels[fileId] || null;
};

export const uploadVideo = (event: any, opts: any, flux: any): (() => void) => {
  opts = opts || {};
  const {onStart, onTranscoded, onFail, onCancel} = opts;

  if (event.target.files.length !== 1)
    throw Error('expected to receive HTML event');

  // @ts-expect-error - TS7034 - Variable 'file' implicitly has type 'any' in some locations where its type cannot be determined.
  let file = null;
  // @ts-expect-error - TS7034 - Variable '_cancel' implicitly has type 'any' in some locations where its type cannot be determined.
  let _cancel = null;

  const _onStart = (f: FileRecord) => {
    file = f;

    onStart(f);

    addGlobalCancel();

    return f;
  };

  const _onDone = (data: ScheduledTranscode) => {
    setLocalCancel(
      _pollTranscodeFiles(
        data,
        () => {
          flux.dispatch(
            AssetsActionCreators.updateTranscodeProgress({
              // @ts-expect-error - TS7005 - Variable 'file' implicitly has an 'any' type.
              id: file._id,
              progress: null,
            })
          );

          onTranscoded(data);
        },
        _makeTranscodePollHandler(data.use_media_convert)
      )
    );
  };

  const _makeTranscodePollHandler = (useRealProgress = false) => {
    let pollCount = 0;

    return (percentComplete?: number) => {
      let progress;

      if (useRealProgress && typeof percentComplete === 'number') {
        // Use real progress from MediaConvert
        progress = percentComplete / 100;
      } else {
        // Fake the transcode progress since ElasticTranscoder doesn't provide us a numerical
        // representation of it.
        pollCount = pollCount > 90 ? pollCount : pollCount + 4;
        progress = pollCount / 100;
      }

      flux.dispatch(
        // @ts-expect-error - TS7005 - Variable 'file' implicitly has an 'any' type.
        AssetsActionCreators.updateTranscodeProgress({id: file._id, progress})
      );
    };
  };

  const _pollTranscodeFiles = (
    data: ScheduledTranscode,
    successHandler: () => void,
    pollHandler: (percentComplete?: number) => void
  ) => {
    // @ts-expect-error - TS7034 - Variable '_cancelTranscode' implicitly has type 'any' in some locations where its type cannot be determined.
    let _cancelTranscode = null;

    const recurMediaConvert = () => {
      let isRequestLive = true;

      _cancelTranscode = () => {
        isRequestLive = false;
      };

      const siteId = getSiteId(flux)();
      // @ts-expect-error - TS7005 - Variable 'file' implicitly has an 'any' type.
      const fileId = file.get('_id');

      fetch(`/api/sites/${siteId}/transcode/status/${fileId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then((response) => response.json())
        .then((statusData) => {
          if (isRequestLive) {
            if (statusData.status === 'COMPLETE') {
              // Snap UI to full and finalize
              try {
                pollHandler(100);
              } catch (e) {
                undefined;
              }
              successHandler();
              _cancelTranscode = null;
              isRequestLive = false;
            } else if (
              statusData.status === 'ERROR' ||
              statusData.status === 'CANCELED'
            ) {
              throw new Error(statusData.error_message || 'Transcoding failed');
            } else {
              // Update with real progress
              pollHandler(statusData.percent_complete);

              let timeout = setTimeout(() => {
                if (timeout !== null) {
                  recurMediaConvert();
                }
              }, 5000);

              _cancelTranscode = () => {
                clearTimeout(timeout);
                // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'Timeout'.
                timeout = null;
              };
            }
          }
        })
        .catch((error) => {
          console.error('Transcode polling error:', error);
          // Continue polling on error (transient failures)
          if (isRequestLive) {
            let timeout = setTimeout(() => {
              if (timeout !== null) {
                recurMediaConvert();
              }
            }, 5000);

            _cancelTranscode = () => {
              clearTimeout(timeout);
              // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'Timeout'.
              timeout = null;
            };
          }
        });
    };

    const recurS3 = (pollUrl: string) => {
      let isRequestLive = true;

      _cancelTranscode = () => {
        isRequestLive = false;
      };

      fetch(pollUrl, {
        method: 'HEAD',
        mode: 'cors',
        cache: 'default',
      }).then((response) => {
        if (isRequestLive) {
          if (response.status >= 200 && response.status < 300) {
            successHandler();
            _cancelTranscode = null;
          } else {
            pollHandler();

            let timeout = setTimeout(() => {
              if (timeout !== null) {
                recurS3(pollUrl);
              }
            }, 5000);

            _cancelTranscode = () => {
              clearTimeout(timeout);
              // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'Timeout'.
              timeout = null;
            };
          }
        }
      });
    };

    const cancelTranscode = () => {
      // @ts-expect-error - TS7005 - Variable '_cancelTranscode' implicitly has an 'any' type.
      if (_cancelTranscode) {
        _cancelTranscode();
      }
    };

    // Start polling based on method
    if (data.use_media_convert) {
      // TypeScript knows data is MediaConvertResponse
      recurMediaConvert();
    } else {
      // TypeScript knows data is ElasticTranscoderResponse with poll_url
      recurS3(data.poll_url);
    }

    return cancelTranscode;
  };

  const _onFail = () => {
    onFail({
      message: 'There was an error while uploading your video.',
    });

    setLocalCancel(null);

    cleanupGlobalCancel();
  };

  const doUpload = () => {
    let isUploadLive = true;

    const defaultHelpers: AssetHelperFunctions = {
      isSigned: ({type, asset, mintId}) => {
        return type === ASSET_SIGNING_FINISHED && asset._id === mintId;
      },
      isDone: ({type, payload, meta}, mintId, realId) => {
        return (
          (type === ASSET_UPLOAD_FINISHED && meta === realId) ||
          (type === ASSET_S3_UPLOAD_NOT_NEEDED &&
            payload.meta.signingResponse.get('_id') === realId)
        );
      },
      getDoneAsset: (realId, assetStore) => {
        // Note: It is important that we read AssetStore state with `flux.getStoreState`
        // because we must rely on the state that is being still updated on ASSET_UPLOAD_FINISHED.
        const {assets} = assetStore;
        return assets.get(realId);
      },
      isFailed: ({type, payload, meta}, mintId, realId) =>
        (type === VIDEO_READ_FOR_VALIDATION_FAILED &&
          payload.meta.asset._id === mintId) ||
        (type === ASSET_SIGNING_FAILED && meta._id === mintId) ||
        (type === ASSET_UPLOAD_FAILED && meta === realId),
    } as const;

    uploadSiteVideo({
      blob: event.target.files[0],
      onSign: (asset) => {
        if (isUploadLive) {
          _onStart(asset);
        }
      },
      onDone: (asset) => {
        if (isUploadLive) {
          file = asset;

          scheduleTranscode(asset).then(_onDone, _onFail);
        }
      },
      onFail: () => {
        if (isUploadLive) {
          _onFail();
        }
      },
      flux,
      initialActionCreator: videoSelectedForUpload,
      getSiteOrDbIdParams: () => ({siteId: getSiteId(flux)()}),
      ...defaultHelpers,
    });

    setLocalCancel(() => {
      isUploadLive = false;
    });
  };

  const cancel = () => {
    // @ts-expect-error - TS7005 - Variable '_cancel' implicitly has an 'any' type.
    if (_cancel) {
      _cancel();
      cleanupGlobalCancel();
      onCancel();
    }
  };

  const setLocalCancel = (c: null | (() => void)) => {
    _cancel = c;
  };

  const addGlobalCancel = () => {
    // @ts-expect-error - TS7005 - Variable 'file' implicitly has an 'any' type.
    if (!file) return;
    const fileId = file.get('_id');
    _cancels[fileId] = cancel;
  };

  const cleanupGlobalCancel = () => {
    // @ts-expect-error - TS7005 - Variable 'file' implicitly has an 'any' type.
    if (file) {
      const fileId = file.get('_id');
      _cancels[fileId] = null;
    }
  };

  doUpload();

  return cancel;
};
